import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { NgTemplateOutlet, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { LookupService } from '../../../services/lookup.service';
import { MakeModel, MakeModelFilterData, TreeNode } from '../../../models/make-model.model';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  standalone: true,
  selector: 'app-make-model-filter',
  templateUrl: './make-model-filter.component.html',
  styleUrl: './make-model-filter.component.scss',
  imports: [NgbModule, NgTemplateOutlet, CommonModule, FormsModule, TranslocoPipe],
})
export class MakeAndModelFilterComponent implements OnInit, OnDestroy {
  private lookupService = inject(LookupService);
  private errorService = inject(ErrorHandlerService);

  @Input() disabled: boolean = false;
  @Input() filter: MakeModelFilterData = { makeModels: [] };
  @Input() accountFilterChanged$: Observable<number[]>;
  @Output() filterChanged: EventEmitter<MakeModelFilterData> =
    new EventEmitter<MakeModelFilterData>();

  isLoading: boolean = true;
  searchTerm: string = '';

  // Tree data
  makeModelTree: TreeNode[] = [];
  filteredTree: TreeNode[] = [];
  selectedItems: TreeNode[] = [];
  selectedItemsCount: number = 0;

  private accountFilterSubscription: Subscription;
  private cancelSubject: Subject<void> = new Subject<void>();
  selectedAccounts: Array<number> = [];

  ngOnInit(): void {
    this.initAccountListener();
  }

  ngOnDestroy(): void {
    this.accountFilterSubscription?.unsubscribe();
    this.cancelSubject?.next();
  }

  private initAccountListener(): void {
    if (!this.accountFilterChanged$) {
      return;
    }

    this.accountFilterSubscription = this.accountFilterChanged$.subscribe((accountIds) => {
      this.selectedAccounts = accountIds ?? [];
      // Clear selected items when accounts change
      this.selectedItems = [];
      this.filter.makeModels = [];
      this.updateSelectedCount();
      this.loadMakeModelData();
    });
  }

  async loadMakeModelData(): Promise<void> {
    this.isLoading = true;
    try {
      this.cancelSubject.next();
      const response = await this.lookupService.getMakeModelsAsync(
        this.selectedAccounts,
        this.cancelSubject
      );

      this.makeModelTree = this.buildMakeModelTree(response.data ?? []);
      this.filteredTree = this.makeModelTree;
      this.applyFilter(response.data ?? []);
      this.isLoading = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        },
      });
    }
  }

  buildMakeModelTree(data: MakeModel[]): TreeNode[] {
    const makeMap = new Map<number, TreeNode>();
    let autoId = 1;

    data.forEach((item) => {
      if (!item.makeId) return;

      if (!makeMap.has(item.makeId)) {
        makeMap.set(item.makeId, {
          id: item.makeId,
          name: item.makeName,
          checked: false,
          expanded: false,
          children: [],
          makeModelData: item,
        });
      }

      if (item.modelId) {
        makeMap.get(item.makeId)!.children.push({
          id: item.modelId,
          parentId: item.makeId,
          name: item.modelName,
          checked: false,
          expanded: false,
          children: [],
          makeModelData: item,
        });
      }
    });

    return Array.from(makeMap.values());
  }

  searchMakeModel(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    if (searchTerm) {
      this.filteredTree = this.searchTreeItems(this.makeModelTree, searchTerm);
    } else {
      this.filteredTree = this.makeModelTree;
    }

    // Reapply checked state
    this.filteredTree.forEach((item) => {
      this.applyToggle(item);
    });
  }

  searchTreeItems(items: TreeNode[], searchTerm: string): TreeNode[] {
    return items.reduce((acc: TreeNode[], item) => {
      const parentMatches = item.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchedChildren = this.searchTreeItems(
        item.children,
        searchTerm.toLowerCase()
      );

      if (parentMatches || matchedChildren.length > 0) {
        acc.push({
          ...item,
          // If parent matches, include ALL children. Otherwise, only include matched children
          children: parentMatches ? item.children : matchedChildren,
        });
      }

      return acc;
    }, []);
  }

  onCheckboxChange(item: TreeNode): void {
    this.toggleCheckBox(item);
    this.updateFilter();
    this.updateSelectedCount();
  }

  private toggleCheckBox(item: TreeNode): void {
    const index = this.selectedItems.findIndex((x) => x.id === item.id);

    if (index === -1) {
      this.selectedItems.push(item);
      item.checked = true;
    } else {
      this.selectedItems.splice(index, 1);
      item.checked = false;
    }

    // Toggle all children
    item.children.forEach((child) => {
      this.toggleChildren(child, item.checked);
    });
  }

  private toggleChildren(item: TreeNode, toggle: boolean): void {
    if (toggle) {
      if (!this.selectedItems.find((x) => x.id === item.id)) {
        this.selectedItems.push(item);
        item.checked = true;
      }
    } else {
      const index = this.selectedItems.findIndex((x) => x.id === item.id);
      if (index !== -1) {
        this.selectedItems.splice(index, 1);
      }
      item.checked = false;
    }

    item.children.forEach((child) => {
      this.toggleChildren(child, item.checked);
    });
  }

  private applyToggle(item: TreeNode): void {
    item.checked = !!this.selectedItems.find((x) => x.id === item.id);
    item.children.forEach((child) => {
      this.applyToggle(child);
    });
  }

  private applyFilter(data: MakeModel[]): void {
    if (this.filter.makeModels && this.filter.makeModels.length > 0) {
      // Rebuild tree to match selected items
      this.selectedItems = [];
      const tempTree = this.buildMakeModelTree(data);

      this.filter.makeModels.forEach((filterItem: MakeModel) => {
        this.findAndSelectNode(tempTree, filterItem);
      });

      this.updateSelectedCount();
    }
  }

  private findAndSelectNode(nodes: TreeNode[], makeModel: MakeModel): void {
    for (const node of nodes) {
      if (node.makeModelData) {
        const matches =
          node.makeModelData.makeId === makeModel.makeId &&
          (makeModel.modelId
            ? node.makeModelData.modelId === makeModel.modelId
            : true);

        if (matches) {
          if (!this.selectedItems.find((x) => x.id === node.id)) {
            this.selectedItems.push(node);
            node.checked = true;
          }
        }
      }

      if (node.children.length > 0) {
        this.findAndSelectNode(node.children, makeModel);
      }
    }
  }

  private updateFilter(): void {
    this.filter.makeModels = this.selectedItems
      .filter((item) => item.parentId !== undefined)
      .map((item) => item.makeModelData!);

    this.filterChanged.emit(this.filter);
  }

  private updateSelectedCount(): void {
    this.selectedItemsCount = this.selectedItems.filter(
      (item) => item.parentId !== undefined
    ).length;
  }
}
